import React, { useCallback, useState, useRef, useMemo } from 'react'
import { Portal } from 'react-portal'

import { CloseIcon } from '../icons'
import Input from '../input'

import S from './styled'

const getOffsetRect = elem => {
  let box = elem.getBoundingClientRect()
  let body = document.body
  let docElem = document.documentElement
  let scrollTop = window.pageYOffset || docElem.scrollTop || body.scrollTop
  let scrollLeft = window.pageXOffset || docElem.scrollLeft || body.scrollLeft
  let clientTop = docElem.clientTop || body.clientTop || 0
  let clientLeft = docElem.clientLeft || body.clientLeft || 0
  let top  = box.top +  scrollTop - clientTop
  let left = box.left + scrollLeft - clientLeft
  return { top: Math.round(top), left: Math.round(left) }
}

const getMenuRectOffset = elem => {
  const offsets = { x: 0, y: 0 }
  if (elem) {
    const bounding = elem.getBoundingClientRect()
    if (bounding.bottom > (window.innerHeight || document.documentElement.clientHeight)) {
      offsets.y = - 8 - (bounding.bottom - (window.innerHeight || document.documentElement.clientHeight))
    }
    if (bounding.right > (window.innerWidth || document.documentElement.clientWidth)) {
      offsets.x = - 80 - (bounding.right - (window.innerWidth || document.documentElement.clientWidth))
    }
  }
  return offsets
}


const Tag = ({ tag, onDelete }) => (
  <S.TagContainer>
    <S.Tag key={`tag_${tag.id}`}>{tag.description}</S.Tag>
    {onDelete && <S.Close onClick={() => onDelete(tag.id)}><CloseIcon/></S.Close>}
  </S.TagContainer>
)

const AddTagButton = ({ tag, value = [], values = [], onAdd }) => {

  const [menuOpened, setMenuOpened] = useState(false)
  const [search, setSearch] = React.useState('')
  const [focused, setFocused] = useState(null)
  const [menuOffset, setMenuOffset] = useState({ x: 0, y: 0 })

  const menuEl = useRef(null)
  const buttonRef = useRef(null)

  const handleClickOutside = useMemo(() => {
    return event => {
      if (menuEl.current && !menuEl.current.contains(event.target)) {
        document.removeEventListener('mousedown', handleClickOutside)
        setMenuOpened(false)
        setFocused(null)
        setSearch('')
        setMenuOffset({ x: 0, y: 0 })
      }
    }
  }, [])

  const hideMenu = useCallback(() => {
    document.removeEventListener('mousedown', handleClickOutside)
    setMenuOpened(false)
    setFocused(null)
    setSearch('')
    setMenuOffset({ x: 0, y: 0 })
  }, [handleClickOutside])

  const showMenu = useCallback(() => {
    document.addEventListener('mousedown', handleClickOutside)
    setMenuOpened(true)
  }, [handleClickOutside])

  const valuesList = (values || [])
    .filter(tag => !(value || []).includes(tag.id))
    .filter(tag => search ? tag.description.toLocaleLowerCase().includes(search.toLocaleLowerCase()) : true)
    .sort((tag1, tag2) => tag1.description.localeCompare(tag2.description))
  const valuesToRender = [...valuesList]
  let shouldAddSearch = values.length > 10
  if (valuesToRender.length > 10) {
    valuesToRender.length = 10
  }

  const handleAdd = useCallback((e,id) => {
    e.stopPropagation()
    onAdd(id)
    hideMenu()
  }, [hideMenu, onAdd])

  const input = useMemo(() => () => ({
    onChange: e => setSearch(e.target.value),
    value: search,
  }), [search])

  const keysHandler = useMemo(() => event => {
    if (event.keyCode === 38) {
      // up arrow
      if (!focused && focused !== 0) {
        setFocused(0)
      } else {
        setFocused(Math.max(focused - 1, 0))
      }
      event.stopPropagation()
      event.preventDefault()
    } else if (event.keyCode === 40) {
      if(!focused && focused !== 0) {
        setFocused(0)
      } else {
        setFocused(Math.min(focused + 1, valuesToRender.length - 1))
      }
      event.stopPropagation()
      event.preventDefault()
    } else if (event.key === 'Enter' && focused <= valuesToRender.length) {
      handleAdd(event, valuesToRender[focused].id)
    } else if(event.key === 'Escape') {
      hideMenu()
    }
  }, [focused, handleAdd, hideMenu, valuesToRender])

  React.useEffect(() => {
    if (menuOpened) {
      setMenuOffset(getMenuRectOffset(menuEl.current))
    }
  }, [menuOpened])

  return (
    <S.TagContainer ref={buttonRef}>
      <S.AddTag onClick={showMenu}>+ADD</S.AddTag>
      {menuOpened && (
        <Portal><S.Menu ref={menuEl} style={{
          top: getOffsetRect(buttonRef.current).top + menuOffset.y + buttonRef.current.offsetHeight + 2,
          left: getOffsetRect(buttonRef.current).left + menuOffset.x,
        }}>
          {
            shouldAddSearch && (
              <Input
                style={{ padding: 0 }}
                autoFocus
                {...{
                  name: 'search',
                  text: input,
                  placeholder: 'Type name to search',
                }}
                onKeyDown={keysHandler}
              />
            )
          }
          {
            shouldAddSearch && <S.Small>{valuesToRender.length} out of {valuesList.length}</S.Small>
          }
          {
            valuesToRender.map((tag, index) => (
              <S.MenuItem key={`menu-item-${tag.id}`} onClick={e => handleAdd(e, tag.id)} className={focused === index ? 'focused' : ''}>
                {tag.description}
              </S.MenuItem>
            ))
          }
          {
            valuesList.length > 10 && <S.Small>continue search to see more</S.Small>
          }
        </S.Menu></Portal>
      )}
    </S.TagContainer>
  )
}

const Tags = ({ title, name, value = [], values = [], selected, onChange, disabled }) => {
  const handleDelete = useCallback(id => {
    onChange({
      target: {
        name, value: value.filter(value => value !== id), type: 'list',
      },
    })
  }, [name, onChange, value])

  const handleAdd = useCallback(id => {
    onChange({
      target: {
        name, value: [...(value || []), id],
      },
    })
  }, [name, onChange, value])

  return (
    <S.TagsContainer className={disabled ? 'disabled' : ''}>
      {title && <label>{title}</label>}
      <S.Tags>
        {
          (value || []).map(item => {
            const tagToUse =  values.find(tag => tag.id === item)
            return (
              tagToUse &&
              <Tag key={`tag_${item}`} tag={tagToUse} onDelete={disabled ? null : handleDelete} />
            )
          })
        }
        {
          !disabled && (value || []).length !== (values || []).length && (values || []).length && (
            <AddTagButton value={value} values={values} onAdd={handleAdd}/>
          )
        }
        {
          !(values || []).length && <S.Info>No options avaiable</S.Info>
        }
      </S.Tags>
    </S.TagsContainer>
  )
}

export default Tags
